import { feedTopGiocate } from 'lib/ssr/sport/top-giocate';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { sportsCli, SportsWidgetPiuGiocateEnum } from 'types/swagger';
import { normalizeSlug } from 'utility/functions';
import { HubConnection } from '@microsoft/signalr';
import { useTypedSelector } from 'lib/centralStore';
import { selectSignalRConnection } from 'features/signalR/selectors';
import { SignalRs } from 'features/signalR/signalRSlice';
import { selectTopGiocateTabs } from 'features/sport/selectors';

interface SportPiuGiocateHookType {
  isLoading: boolean;
  doFetch: (_: SportsWidgetPiuGiocateEnum) => void;
}

export const useSportPiuGiocateHook = (): SportPiuGiocateHookType => {
  const dispatch = useDispatch();
  const signalRHub = useTypedSelector(selectSignalRConnection);

  const { asPath } = useRouter();
  const { selected } = useTypedSelector(selectTopGiocateTabs);

  const refSlug = useRef('');
  const refSelected = useRef('');

  const [isLoading, setLoading] = useState(false);

  const signalRHubConnection = signalRHub(SignalRs.sport);

  const connectionSlug = useCallback(
    async (slug: string, activeTab: SportsWidgetPiuGiocateEnum) => {
      try {
        if (!signalRHubConnection) return;

        if (slug && (signalRHubConnection as HubConnection).state === 'Connected') {
          if (activeTab === SportsWidgetPiuGiocateEnum.Live) {
            await signalRHubConnection.send('SubscribeToGroup', slug);
          } else {
            await signalRHubConnection.send('UnsubscribeFromGroup', slug);
          }
        }
      } catch (error) {
        console.error('Errore nella sottoscrizione:', error);
        throw error;
      }
    },
    [signalRHubConnection]
  );

  useEffect(() => {
    const slug = normalizeSlug(asPath, true);

    if (slug !== refSlug.current || refSelected.current !== selected) {
      refSlug.current = slug;
      refSelected.current = selected;

      connectionSlug(slug, selected);
    }

    return () => {
      connectionSlug(refSlug.current, SportsWidgetPiuGiocateEnum.ProssimiEventi);
    };
  }, [asPath, selected]);

  const doFetch = async (type: SportsWidgetPiuGiocateEnum) => {
    setLoading(true);

    const { ok, data } = await sportsCli.sports.widgetPiuGiocateList({ slug: refSlug.current, type });

    if (ok) {
      Reflect.set(data, 'type', type);
      dispatch(feedTopGiocate(data));
    } else {
      dispatch(feedTopGiocate({ type } as any));
    }

    setLoading(false);
  };

  return {
    isLoading,
    doFetch,
  };
};
